// AGC: TopBar - Header component for Classic Navy Theme
// AGC: Navy-style top bar with white text
import React from 'react'
import { ChevronLeft } from 'lucide-react'

interface TopBarProps {
  title: string
  subtitle?: string
  onBack?: () => void
  actions?: React.ReactNode
  transparent?: boolean
  className?: string
  // AGC: All TopBar pages are rendered inside MainLayout whose AppHeader already
  // AGC: handles safe-area-inset-top. Default true to avoid double padding.
  // AGC: Set false only for pages rendered outside MainLayout (standalone WebView).
  noSafeAreaTop?: boolean
}

/**
 * AGC: TopBar - Dynamic theme header
 * AGC: Features:
 *   - Uses hero-gradient CSS variable for dynamic theming
 *   - White text/icons
 *   - Back button, title, optional subtitle
 *   - Action buttons slot
 */
export const TopBar: React.FC<TopBarProps> = ({
  title,
  subtitle,
  onBack,
  actions,
  transparent = false,
  className = '',
  noSafeAreaTop = true,
}) => {
  return (
    <header
      className={`sticky top-0 z-30 ${className}`}
      style={{
        background: transparent ? 'transparent' : 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)',
        // AGC: Respect Android system status bar / iOS notch. Without this,
        // AGC: `sticky top-0` puts the bar at pixel 0 of the WebView which on
        // AGC: Android edge-to-edge devices sits UNDERNEATH the system status
        // AGC: bar — the back button becomes physically unreachable. Reported
        // AGC: from PolygonDrawingMap on RQCX102JR5H (Android 15). Fixing it
        // AGC: here in the shared component fixes every page that uses TopBar.
        // AGC: Skip when inside MainLayout — AppHeader already handles safe-area-top.
        ...(!noSafeAreaTop ? { paddingTop: 'env(safe-area-inset-top)' } : {}),
      }}
    >
      <div className="flex items-center justify-between px-4 py-3">
        {/* AGC: Left section - Back button and title */}
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1 -ml-1 rounded-lg hover:bg-white/10 transition-colors duration-200"
              aria-label="Voltar"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}
          <div>
            <h1 className="text-lg font-semibold text-white leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-white/70">{subtitle}</p>
            )}
          </div>
        </div>

        {/* AGC: Right section - Action buttons */}
        {actions && (
          <div className="flex items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    </header>
  )
}

export default TopBar
